import { useCallback } from 'react';
import { useFilter } from '@spa/features/search_job/pc/contexts/FilterContext';
import { trackClickJobFilterSearch } from "@spa/features/search_job/pc/api/analytics/trackEvent";
import {
    getSendConditions,
    normalizeSalaryValues
} from '@src/common/service/salary/salaryHelper';

/**
 * 給与フィルターの適用処理を担当するカスタムフック
 * 
 * @param {Object} params - パラメータ
 * @param {string} params.salaryTypeParamKey - 給与種別パラメータキー
 * @param {string} params.salaryMinParamKey - 給与最小値パラメータキー
 * @param {string} params.salaryMaxParamKey - 給与最大値パラメータキー
 * @param {Object} params.salaryStatistics - 給与統計データ
 * @param {string} params.channelName - チャネル名
 * @returns {Object} フィルター適用処理の結果
 */
const useSalaryFilterActions = ({
    salaryTypeParamKey,
    salaryMinParamKey,
    salaryMaxParamKey,
    salaryStatistics,
    channelName,
}) => {
    const { handleFilterChange } = useFilter();

    /**
     * フィルター適用時の処理
     * @param {Object} localState - ローカル状態
     * @param {Object} sliderInstanceRef - スライダーインスタンスの参照
     * @param {Function} onComplete - 完了時のコールバック
     */
    const handleApply = useCallback((localState, sliderInstanceRef, onComplete) => {
        trackClickJobFilterSearch(channelName);

        // スライダー値を優先して取得
        let updatedMin = localState.salaryMin;
        let updatedMax = localState.salaryMax;

        if (sliderInstanceRef.current) {
            const sliderValues = sliderInstanceRef.current.get();
            if (Array.isArray(sliderValues) && sliderValues.length === 2) {
                updatedMin = sliderValues[0];
                updatedMax = sliderValues[1];
            }
        }

        // 給与値を正規化
        const { min: normalizedMin, max: normalizedMax } = normalizeSalaryValues(updatedMin, updatedMax);

        // min, max の送信判定（スライダー範囲の端の値は送信しない）
        const { shouldSendMin, shouldSendMax, isAll } = getSendConditions({
            typeId: localState.salaryType,
            min: normalizedMin,
            max: normalizedMax,
            salaryStatistics
        });

        // フィルター更新（遅延実行でページ遷移後にstate更新）
        setTimeout(() => {
            const updates = {
                [salaryTypeParamKey]: isAll ? null : localState.salaryType,
                [salaryMinParamKey]: shouldSendMin ? normalizedMin : null,
                [salaryMaxParamKey]: shouldSendMax ? normalizedMax : null
            };
            handleFilterChange(updates);
            
            // 完了時のコールバック実行
            if (onComplete) {
                onComplete();
            }
        }, 0);
    }, [handleFilterChange, salaryTypeParamKey, salaryMinParamKey, salaryMaxParamKey, channelName, salaryStatistics]);

    /**
     * クリアボタンクリック時の処理
     * @param {Event} e - イベントオブジェクト
     * @param {Function} onComplete - 完了時のコールバック
     */
    const handleClearClick = useCallback((e, onComplete) => {
        e.preventDefault();
        e.stopPropagation();
        trackClickJobFilterSearch(channelName);

        // filter.js 実行後にReactのクリア処理を実行
        setTimeout(() => {
            const updates = {
                [salaryTypeParamKey]: null,
                [salaryMinParamKey]: null,
                [salaryMaxParamKey]: null
            };
            handleFilterChange(updates);
            
            // 完了時のコールバック実行
            if (onComplete) {
                onComplete();
            }
        }, 0);
    }, [handleFilterChange, salaryTypeParamKey, salaryMinParamKey, salaryMaxParamKey, channelName]);

    return {
        handleApply,
        handleClearClick,
    };
};

export default useSalaryFilterActions; 